import { z } from "zod";
import { PHONE_ERROR, isValidPhone, normalizePhone } from "./phone";
import { vendorAcknowledgementOptions } from "./form-constants";
import { culturalBoothEvents, foodPermitStatuses, foodVendorEvents, foodVendorTypes, villageDays, villageIndustries, villageInterests, villagePackages, villageSegments } from "./village-intake";

// Pure schemas shared by the step renderer and authenticated server writes.
const text = z.string().trim().min(1, "Field is required").max(4000);
const email = z.string().trim().email("Invalid email address").max(254);
const consent = z.literal("Yes", { errorMap: () => ({ message: "You must accept to submit." }) });
const url = z.string().trim().max(2048).url().refine((value) => /^https?:\/\//i.test(value), "Use an http or https URL.");
const days = z.array(z.enum(villageDays)).min(1, "Select at least one option.").max(3).refine(
  (values) => !values.includes("Not sure yet") || values.length === 1,
  "Choose dates or Not sure yet, not both."
);

export const villageInterestSchema = {
  firstName: text, lastName: text, email, city: text,
  villageInterests: z.array(z.enum(villageInterests)).min(1, "Select at least one interest.").max(villageInterests.length),
  participationDays: days, updatesConsent: consent,
};

export const villageVendorSchema = {
  exhibitorSegment: z.enum(villageSegments), businessName: text, firstName: text, lastName: text,
  email, phoneNumber: z.preprocess((v) => (typeof v === "string" ? normalizePhone(v) : v), z.string().max(40).refine(isValidPhone, PHONE_ERROR)).describe("phone"),
  city: text, organizationWebsite: url.or(z.literal("")).optional(), primaryIndustry: z.enum(villageIndustries),
  jobOpenings: text, careersUrl: url, startupSummary: text, hiringPlans: text,
  programDescription: text, programAudience: text, itemsDescription: text,
  vendorAcknowledgement: z.array(z.enum(vendorAcknowledgementOptions)).max(vendorAcknowledgementOptions.length).refine(
    (values) => vendorAcknowledgementOptions.every((option) => values.includes(option)),
    "Acknowledge every prohibited category."
  ),
  culturalShowcase: text,
  foodVendorType: z.enum(foodVendorTypes), foodOffering: text, foodPermitStatus: z.enum(foodPermitStatuses),
  foodEvents: z.array(z.enum(foodVendorEvents)).min(1, "Select at least one option.").max(foodVendorEvents.length),
  boothEvents: z.array(z.enum(culturalBoothEvents)).min(1, "Select at least one option.").max(culturalBoothEvents.length),
  partnershipGoals: text, participationDays: days, packagePreference: z.enum(villagePackages), applicationConsent: consent,
};

export function isVillageIntake(formId: string): formId is "vendor" | "village-interest" {
  return formId === "vendor" || formId === "village-interest";
}
